import React from 'react'
import { GraviButton, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Tooltip, Popconfirm } from 'antd'
import { LinkOutlined } from '@ant-design/icons'
import type { MatchInvoice, MatchBol } from '@api/useSupplierReconciliation/types/match'

interface PendingItem {
  type: 'invoice' | 'bol'
  id: string
}

interface ActionHandlers {
  handleAddToMatch: (type: 'invoice' | 'bol', item: MatchInvoice | MatchBol, itemId: string, matchId?: string) => void
  handleRemoveFromMatch: (type: 'invoice' | 'bol', itemId: string, item?: MatchInvoice | MatchBol) => void
  pendingItems: PendingItem[]
}

interface HelperFunctions {
  isItemMatched: (item: MatchInvoice | MatchBol) => boolean
}

interface ManageMatchActionRendererProps {
  data: MatchInvoice | MatchBol
  actions: ActionHandlers
  helpers: HelperFunctions
  searchType: 'invoice' | 'bol'
}

export function ManageMatchActionRenderer({ data, actions, helpers, searchType }: ManageMatchActionRendererProps) {
  const item = data
  const itemId = searchType === 'invoice' ? item?.invoice_id : item?.bol_id
  const isInMatch = item?.inMatch
  const isMatched = helpers.isItemMatched(item)

  const [isPending, setIsPending] = React.useState(() => {
    return actions.pendingItems.some((pending: PendingItem) => pending.type === searchType && pending.id === itemId)
  })

  // Listen for updates to pending items
  React.useEffect(() => {
    const checkPendingStatus = () => {
      const pending = actions.pendingItems.some(
        (pending: PendingItem) => pending.type === searchType && pending.id === itemId
      )
      setIsPending(pending)
    }

    // Check immediately and set up interval
    checkPendingStatus()
    const interval = setInterval(checkPendingStatus, 100)

    return () => clearInterval(interval)
  }, [actions.pendingItems, searchType, itemId])

  // For items in match, show different button
  if (isInMatch) {
    return (
      <GraviButton
        size='small'
        appearance='outlined'
        error
        onClick={() => {
          actions.handleRemoveFromMatch(searchType, itemId, item)
        }}
        style={{
          height: 28,
          fontSize: 12,
        }}
      >
        Remove from Match
      </GraviButton>
    )
  }

  if (isPending) {
    return (
      <GraviButton
        size='small'
        appearance='outlined'
        error
        onClick={() => {
          // Note: handleRemovePending not implemented in actions yet
          // This functionality may need to be added to actions interface
          console.warn('handleRemovePending not available in actions interface')
          setIsPending(false)
        }}
        style={{
          height: 28,
          fontSize: 12,
        }}
      >
        Remove
      </GraviButton>
    )
  }

  // Helper function to handle add to match action
  const handleAddToMatchAction = () => {
    actions.handleAddToMatch(searchType, item, itemId)
    setIsPending(true)
  }

  // If item is already matched to another match, show confirmation popover
  if (isMatched && !isInMatch) {
    return (
      <Popconfirm
        title={`This ${searchType} is matched: add all attached items to this match?`}
        onConfirm={handleAddToMatchAction}
        okText='OK'
        cancelText='Cancel'
        placement='left'
      >
        <GraviButton theme2 size='small' style={{ height: 28, fontSize: 12 }}>
          Add To Match
        </GraviButton>
      </Popconfirm>
    )
  }

  // Regular add to match button for unmatched items
  return (
    <GraviButton theme2 size='small' onClick={handleAddToMatchAction} style={{ height: 28, fontSize: 12 }}>
      Add To Match
    </GraviButton>
  )
}
